import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useManagerMeApi, type ManagerMeData, type GetMeResult } from '@/services/managers'
import { useAuthStore } from '@/stores/auth'

export interface User {
  id: string
  name: string
  email: string
  role: 'admin' | 'manager' | 'viewer'
  avatarUrl?: string
}

export const useUserStore = defineStore('user', () => {
  const user = ref<User | null>(null)
  const meData = ref<ManagerMeData | null>(null)
  /** Resultado da última chamada a /manager/me (sucesso ou erro) para redirecionamentos. */
  const meLoadResult = ref<GetMeResult | null>(null)
  const isLoading = ref(false)

  const isAuthenticated = computed(() => !!user.value)
  const userName = computed(() => user.value?.name || '')
  const userEmail = computed(() => user.value?.email || '')
  const companyName = computed(() => meData.value?.account?.company_name || '')
  const userAvatarUrl = computed(() => user.value?.avatarUrl ?? '')
  const userInitials = computed(() => {
    if (!user.value?.name) return '?'
    const names = user.value.name.trim().split(' ')
    if (names.length >= 2) {
      return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase()
    }
    return user.value.name.substring(0, 2).toUpperCase()
  })

  const setUser = (userData: User) => {
    user.value = userData
  }

  const clearUser = () => {
    user.value = null
    meData.value = null
    meLoadResult.value = null
  }

  /**
   * Zera o cache de GET /manager/me quando o contexto da conta muda (ex.: POST /manager/accounts/select).
   * Sem isso, `ensureMeLoaded()` não refetch: o header `X-ACCOUNT-ID` já é da conta nova, mas `meData`/`meLoadResult`
   * continuam da conta antiga — onboarding e permissões ficam incoerentes e outras rotas podem responder 403.
   */
  const invalidateManagerMe = () => {
    user.value = null
    meData.value = null
    meLoadResult.value = null
  }

  const fetchUser = async () => {
    if (meLoadResult.value !== null) return
    isLoading.value = true
    try {
      const { getMe } = useManagerMeApi()
      const authStore = useAuthStore()
      const result = await getMe()
      meLoadResult.value = result
      if (result.ok && result.data) {
        const data = result.data
        meData.value = data
        const account = data.account
        authStore.setBusinessCnpj((account?.cnpj as string) ?? null)
        user.value = {
          id: (account?.id as string) ?? authStore.accountId ?? '',
          name: (account?.name as string) ?? '',
          email: (account?.email as string) ?? '',
          role: 'admin',
        }
      } else {
        meData.value = null
        user.value = null
      }
    } catch {
      meData.value = null
      user.value = null
      meLoadResult.value = null
    } finally {
      isLoading.value = false
    }
  }

  /** Garante que /manager/me foi carregado (uma única chamada); usado por páginas que precisam de meData. */
  const ensureMeLoaded = async (): Promise<void> => {
    if (meLoadResult.value !== null) return
    await fetchUser()
  }

  /** Rebusca /manager/me e atualiza a store (ex.: poll na tela in-analysis). */
  const refreshMe = async (): Promise<GetMeResult> => {
    const { getMe } = useManagerMeApi()
    const authStore = useAuthStore()
    const result = await getMe()
    meLoadResult.value = result
    if (result.ok && result.data) {
      const data = result.data
      meData.value = data
      const account = data.account
      authStore.setBusinessCnpj((account?.cnpj as string) ?? null)
      user.value = {
        id: (account?.id as string) ?? authStore.accountId ?? '',
        name: (account?.name as string) ?? '',
        email: (account?.email as string) ?? '',
        role: 'admin',
      }
    } else {
      meData.value = null
      user.value = null
    }
    return result
  }

  return {
    user,
    meData,
    meLoadResult,
    isLoading,
    isAuthenticated,
    userName,
    userEmail,
    companyName,
    userAvatarUrl,
    userInitials,
    setUser,
    clearUser,
    invalidateManagerMe,
    fetchUser,
    ensureMeLoaded,
    refreshMe,
  }
})
